import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ApplicationRef, Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { LoginService } from '@ebw-monorepo/login';
import { safeJsonClone, safeJsonParse } from '@ebw-monorepo/microservices-helpers/utils/parser';
import { EbwModalComponent, EbwModalService } from '@ebw-monorepo/modal';
import { UtilsService } from '@ebw-monorepo/utils';
import * as moment from 'moment';
import Feature from 'ol/Feature';
import { Coordinate } from 'ol/coordinate';
import { GeoJSON } from 'ol/format';
import Point from 'ol/geom/Point';
import BaseLayer from 'ol/layer/Base';
import VectorLayer from 'ol/layer/Vector';
import { transform } from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import { BehaviorSubject, Observable, Subject, firstValueFrom, lastValueFrom } from 'rxjs';
import { API_URLS, ApiUrlService } from '../../../../utils/src/lib/services/api-url.service';
import { CustomButtonComponent } from '../components/custom-button/custom-button.component';
import { ReportDialogComponent } from '../components/report-dialog/report-dialog.component';
import { parseUrn } from '../utils/urn.utils';
import { MapService } from './map.service';
/**
 * Helper service for the Feature Select feature of the map.
 * It contains global variables to communicate the feature select component with other components, as well as other
 * utility function used by the feature select.
 */
@Injectable({
  providedIn: 'root'
})
export class FeatureSelectService {
  /** Global list of the features that were selected by the click event. */
  featuresSelected: any[] = [];
  activeFeature: Feature;
  tempActiveFeature$: BehaviorSubject<Feature>;
  activeFeature$: Observable<Feature>;

  /** Indicates if there is a feature selected and open. */
  featuresOpen: boolean = false;

  /** The hit radius of the click for the features selection */
  HIT_RADIUS = 5;
  /** The multiplier applied to the map resolution to compute the range for the features selection */
  HIT_RADIUS_FACTOR = 10;

  /** Layer to put the highlight of the feature when it is selected */
  featureHighlightLayer: VectorLayer<any>;

  modalRef: MatDialogRef<EbwModalComponent, any>;
  customModalSubject: BehaviorSubject<{ feature: any }> = new BehaviorSubject<{
    feature: any;
  }>({ feature: null });
  featuresViewerClosedSubject$: Subject<boolean> = new Subject<boolean>();
  featuresSelectedChanged$: Subject<Feature[]> = new Subject<Feature[]>();

  private _collectionConfig: { collections: any[]; enumerators: any[] };
  private _datasetConfig: { name: string; label: string; type: string; queryable?: boolean }[];

  public _avaiableActions = ['view', 'viewCustom'];
  openOtdrPanel$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private mapService: MapService,
    private loginService: LoginService,
    private http: HttpClient,
    private ebwModal: EbwModalService,
    private _appRef: ApplicationRef,
    private _utilsService: UtilsService,
    private _apiUrlService: ApiUrlService
  ) {
    this.featuresSelected = [];
    this.featureHighlightLayer = new VectorLayer({
      source: new VectorSource()
    });
    this.tempActiveFeature$ = new BehaviorSubject<Feature>(this.activeFeature);

    this.mapService.mapConfigurationLoaded$.subscribe((config) => {
      if (config?.mapSetup?.clickSize) {
        this.HIT_RADIUS = config.mapSetup?.clickSize;
      }
    });
  }

  // --------------------------------------------------------------------------------------------------------------

  public setCollectionConfig(config) {
    this._collectionConfig = config;
  }

  public getCollectionConfig() {
    return this._collectionConfig;
  }

  public setDatasetConfig(config) {
    this._datasetConfig = config;
  }

  getFeatureReport() {
    //TODO
  }

  /**
   * Function to search for features in the layers that are currently active and visible on a specified point.
   * @param location The coordinates of the point to search for feature data.
   */
  searchFeaturesInActiveLayers(location: Point) {
    this.featuresSelected = [];

    // Build list of visible layers with their request URLs
    const layerRequests: { dataUrl: string; dataUrlDetail: string; private: boolean }[] = [];
    const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

    this.mapService.activeLayers.forEach((layer: BaseLayer) => {
      const dataUrlRaw = layer.get(MapService.DATA_URL_LAYER_KEY);
      const validUrn = dataUrlRaw && (dataUrlRaw !== 'null' || dataUrlRaw == null);
      if (!validUrn || !layer.getVisible()) {
        return;
      }
      const newres = this.mapService.mapResolution;
      const minres = layer.getMinResolution();
      const maxres = layer.getMaxResolution();
      if (!(newres >= minres && newres < maxres)) {
        return;
      }

      layerRequests.push({
        dataUrl: dataUrlRaw,
        dataUrlDetail: layer.get(MapService.DATA_URL_DETAIL_LAYER_KEY),
        private: uuidRegex.test(layer.get('title'))
      });
    });

    if (layerRequests.length === 0) {
      return;
    }

    this.mapService.loading = true;

    // Build request tuples — keeps promise and its dataUrlDetail together
    const requests = layerRequests.map(({ dataUrl, dataUrlDetail, private: isPrivate }) => {
      let params = new HttpParams();
      const arr = dataUrl.split('?');
      const baseUrl = arr[0];

      if (arr[1]) {
        const qs = new URLSearchParams(arr[1]);
        qs.forEach((val, key) => {
          params = params.set(key, val);
        });
      }

      const map_res = this.mapService.mapResolution;
      const hit_radius = parseInt((this.HIT_RADIUS * Math.max(map_res, 1)).toFixed(0));
      const noconv = params.get('noconv');
      const clickLocation = location.getCoordinates();
      const coords = noconv ? clickLocation : transform(clickLocation, MapService.EPSG_3857_KEY, MapService.EPSG_4326_KEY);

      params = params.set('lng', coords[0].toString());
      params = params.set('lat', coords[1].toString());
      params = params.set('hit_radius', hit_radius.toString());

      const opt = { ...this.getAuthorizationHeader(isPrivate), params };

      return {
        promise: firstValueFrom(this.http.get(baseUrl, opt)),
        dataUrlDetail
      };
    });

    Promise.allSettled(requests.map((r) => r.promise))
      .then((results) => {
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            console.log(`Promise failed with reason:`, (result as PromiseRejectedResult).reason);
            return;
          }
          const response = result.value as any;
          if (!this._checkIfFeatureCollectionIsEmpty(response)) {
            this.parseSelectedFeatures(response.result, requests[index].dataUrlDetail);
          }
        });

        this.mapService.loading = false;
        this.notifyFeaturesSelectedChange();
      })
      .catch((error) => {
        console.error('Get request error', error);
        this.mapService.loading = false;
      });
  }

  parseSelectedFeatures(featureCollection, dataUrlDetail) {
    new GeoJSON().readFeatures(featureCollection, {}).forEach((feature) => {
      try {
        const fid = feature.getId();
        const fids = '' + fid;
        if (!this.featuresSelected.find((el) => el.getId() == fid)) {
          /**
           * TODO Improve the following logic
           * Manually adding to the feature the property detailUrl based on the current layer
           */
          const { config_collection } = this.getConfigDataFromUrn(fids);
          if (config_collection) {
            feature.set('id', parseUrn(fid as string).id);
            feature.set('details', {});
          } else {
            // remove null properties from feature
            const keysToIgnore = ['id', 'deleted'];
            const rawProps = feature.getProperties();
            const props: Record<string, any> = {};
            Object.keys(rawProps).forEach((key) => {
              const value = rawProps[key];
              if (!keysToIgnore.includes(key) && value !== null && value !== 'null') {
                props[key] = value;
              }
            });

            feature.setProperties(props, true);
            feature.set('id', parseUrn(fid as string).id);
            feature.set('details', { id: feature.get('id'), ...feature.getProperties() });
          }

          if (dataUrlDetail) {
            feature.set('detailUrl', dataUrlDetail);
          }

          this.featuresSelected.push(feature);
        }
      } catch (e) {
        console.error(e);
      }
    });
  }

  /**
   * Get the details of a feature
   * @param feature
   */
  getFeatureInfo(feature) {
    return new Promise((resolve, reject) => {
      let detailRequest;

      const detailUrl: string = feature.get('detailUrl');
      const endpoint = detailUrl.split('/').slice(-1)[0].split('?')[0];

      if (endpoint == 'selectByUrns') {
        let opt = { ...this.getAuthorizationHeader() };

        detailRequest = firstValueFrom(this.http.post(detailUrl, { urns: feature.id_ }, opt));
      } else {
        let params = new HttpParams();

        // Feature ID
        params = params.set('urn', feature.id_);
        params = params.set('urns', feature.id_);

        const opt = { ...this.getAuthorizationHeader(), params: params };

        detailRequest = firstValueFrom(this.http.get(detailUrl, opt));
      }

      detailRequest
        .then((response: any) => {
          if (!response || response.length == 0) {
            reject('Feature Info not found');
            return;
          }

          // Area info endpoint returns { prediction, quotations } instead of GeoJSON feature collection
          if (endpoint === 'getAreaInfo') {
            resolve({
              type: 'areaInfo',
              prediction: Array.isArray(response.prediction) ? response.prediction : [],
              quotations: Array.isArray(response.quotations) ? response.quotations : []
            });
            return;
          }

          let featureInfo: any = response.result.features[0] ?? {};
          featureInfo['detailUrl'] = detailUrl;
          featureInfo['id'] = feature.get('id') ?? parseUrn(feature.getId() as string).id;

          resolve(featureInfo);
        })
        .catch((error) => {
          console.error('Get request error', error);
          reject(error);
        });
    });
  }

  getAuthorizationHeader(priv: boolean = false) {
    let token = this.loginService.getToken();

    if (token) {
      let headersObj = {
        Authorization: `Bearer ${token}`
      };

      if (priv) {
        headersObj['user'] = sessionStorage.getItem(LoginService.USER_ID_KEY);
      }

      let headers = new HttpHeaders(headersObj);
      return { headers };
    } else {
      return {};
    }
  }

  /**
   * Call the correct function based on the button clicked
   * @param action
   * @returns
   */
  doAction(action, feature) {
    switch (action) {
      case 'view':
      case 'viewCustom': {
        this.doViewCustom(feature);
        break;
      }
      case 'connectivity': {
        break;
      }
      default: {
        console.log('Action not handled');
        break;
      }
    } // switch
  }

  doViewCustom(feature) {
    let options = {
      width: '850px'
    };

    // Open component in modal
    this.modalRef = this.ebwModal.openCustomModal(
      {
        content: '',
        title: feature.title
      },
      ReportDialogComponent,
      options,
      CustomButtonComponent
    );

    this.customModalSubject.next({ feature });
  }

  objectToArray(obj: MyObject): { key: string; value: any }[] {
    return Object.entries(obj).map(([key, value]) => ({ key, value }));
  }

  mapConfigEnum(config_enum, enumerator, value) {
    if (!config_enum) return undefined;
    const config = config_enum.find((config) => config.name === enumerator);
    if (config) {
      const { values } = config;
      return values[value];
    }
  }

  capitalizeFirstLetter(val: string) {
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
  }

  getConfigDataFromUrn(id: any) {
    if (!id) {
      return undefined;
    }
    const { dataset: dataset_name, collection: collection_name } = parseUrn(id);

    const collectionConfig = this._collectionConfig;
    const datasetConfig = this._datasetConfig;

    const config_collection = collectionConfig.collections.filter(
      (el) => el.name == `${dataset_name}.${collection_name}` || (collection_name ? el.id == collection_name.replace('layer_', '') : false)
    )[0];

    const config_dataset = datasetConfig.filter((el) => el.name == `${dataset_name}`)[0];

    if (!config_collection) {
      return {
        config_collection: null,
        config_enum: null,
        collection_name: null,
        collection_external_name: null,
        dataset_name: null,
        dataset_external_name: null
      };
    }

    const config_enum = collectionConfig.enumerators;
    const collection_external_name = config_collection.external_name || collection_name;
    const dataset_external_name = config_dataset?.label || this.capitalizeFirstLetter(dataset_name);

    return {
      config_collection,
      config_enum,
      collection_name,
      collection_external_name,
      dataset_name,
      dataset_external_name
    };
  }

  async selectFeatureFromSearch(geojson: any): Promise<void> {
    // so per certo che in questo contesto è presente una singola feature
    const selectedFeature = geojson.features && geojson.features.length > 0 ? safeJsonClone(geojson.features[0]) : null;

    if (!selectedFeature) {
      return;
    }

    for (const feature of new GeoJSON().readFeatures(geojson)) {
      try {
        const fid = '' + feature.getId();

        // se la feature ha la geometria la trasformo in 3857
        if (selectedFeature.geometry) {
          selectedFeature.geometry.coordinates = this.mapService.transformGeometryCordsFrom4326To3857(selectedFeature.geometry.coordinates);
        }

        const { config_collection } = this.getConfigDataFromUrn(fid);
        // se l'urn non è classificabile in nessuna collection esco
        if (!config_collection) {
          return;
        }

        feature.set('id', parseUrn(fid).id);

        this.featuresSelected = [feature];
        this.notifyFeaturesSelectedChange();
      } catch (e) {
        console.error(e);
      }
    }
  }

  getPopupLocationForFeatureSelectedBySearch(coordinates): Coordinate {
    // utility per ricavare il centroide di una linea
    const getCentroid = (coords: Coordinate[]): Coordinate => {
      let sumX = 0,
        sumY = 0;
      for (let i = 0; i < coords.length; i++) {
        sumX += coords[i][0];
        sumY += coords[i][1];
      }
      const avgX = sumX / coords.length;
      const avgY = sumY / coords.length;
      return [avgX, avgY];
    };
    // non ho coordinate
    if (!coordinates?.length) {
      return [0, 0];
    }
    // ho un punto
    if (!Array.isArray(coordinates[0])) {
      return coordinates as Coordinate;
    }
    // ho una linea
    if (!Array.isArray(coordinates[0][0])) {
      return getCentroid(coordinates);
    }
    // ho una multilinea
    const coords = coordinates.map((coordArray: Coordinate[]) => getCentroid(coordArray));
    return getCentroid(coords);
  }

  /**
   * Clear the current selected feature removing the map drawing and the popup
   * @param
   * @returns
   */
  clearCurrentFeature(secondTempLayer = false): void {
    // clear the temporary layer to remove the previously drawn elements
    this.mapService.clearAllFeaturesFromTemporaryLayer(secondTempLayer);
    // make the application do a full cycle, this allows the popup component to detect changes between removing the old
    // popup and creating the new one
    this._appRef.tick();
  }

  private _checkIfFeatureCollectionIsEmpty(response) {
    if (!response.result) {
      return true;
    }

    if (response.result.features && response.result.features.length == 0) {
      return true;
    }

    return false;
  }

  // --------------------------------------------------------------------------------

  public async mapFeatureToModal(response: any, action?: string) {
    try {
      const urn = response.getId ? response.getId() : response.id;
      const { config_collection, config_enum } = this.getConfigDataFromUrn(urn);
      if (!config_collection) {
        const featureProperties = response.getProperties();
        response.set('details', response.get('details') ?? safeJsonClone(featureProperties));
        const details = response.get('details') as Record<string, any>;
        delete details.detailUrl;
        delete details.geometry;
        details['title'] = 'Dettaglio';
        details['urn'] = urn;
        return;
      }
      const external_name_arr: any[] = config_collection['fields'];
      const external_name = config_collection['external_name'];

      const keyToField = external_name_arr
        .filter((field) => !!field.visible)
        .reduce(
          (acc, item) => ({
            ...acc,
            [item.external_name]: item.name,
            ...(item.decimal !== undefined && { decimal: item.decimal })
          }),
          {}
        );

      const featureProperties = response.getProperties();
      response.set('details', response.get('details') ?? {});
      const details = response.get('details') as Record<string, any>;

      external_name_arr.forEach((item) => {
        if (!this._hasOwnPropertyCI(featureProperties, item.name.toLowerCase())) {
          return;
        }

        const { external_name: extName, enumerator } = item;
        const field = keyToField[extName];
        if (!field) {
          return;
        }

        let tempValue = this._getCI(featureProperties, item.name);
        // let tempValue = featureProperties[item.name.toLowerCase()];

        if ((item.type === 'date' || item.type === 'datetime') && tempValue) {
          tempValue = moment(tempValue.toString()).format('yyyy/MM/DD');
        }

        if (keyToField['decimal'] && tempValue && typeof tempValue === 'number') {
          tempValue = tempValue.toFixed(keyToField['decimal']).toString();
        }

        // VUS-71: se type = enumerator viene usato il valore diretto perché gss lo ha già convertito in stringa.
        // Il type enumerator servirebbe solo in caso di editing (attualmente non impattante).

        // const mappedEnumValue = this.mapConfigEnum(config_enum, enumerator, tempValue);
        // if (mappedEnumValue) {
        //   tempValue = mappedEnumValue;
        // }

        details[extName] = tempValue ?? '-';
      });

      details['title'] = 'Dettaglio ' + external_name;
      details['urn'] = urn;
      details['config_collection'] = config_collection;

      if (action) {
        this.doAction(action, details);
      }
    } catch (error) {
      console.error('[FeatureSelectService][mapFeatureToModal]', error);
    }
  }

  // --------------------------------------------------------------------------------

  /**
   * Recupera le props di join tra asset
   * @param data array delle prop escluse quelle in join e di collection_config
   * @returns array delle prop escluse quelle in join e di collection_config
   * @returns array delle prop di join
   */
  parseDataForReportDialogUI(data: any): {
    properties: any[];
    joinProperties: any[];
  } {
    try {
      const collectionObj = data.find((el: any) => el.key === 'config_collection');
      // se non trovo la collection non faccio niente
      if (!collectionObj) {
        throw new Error("Collection dell'asset non trovata");
      }
      // ricavo nome collection
      const collection = collectionObj.value;
      // recupero le prop che rappresentano una join con l'asset corrente
      let joinProperties = this.getJoinProperties(collection, data);
      !joinProperties && (joinProperties = []);
      // Adesso elimino le props che non voglio mostrare a video
      // collection
      const isCollectionProp = (el: any) => el.key === 'config_collection';
      // urn
      const isUrnProp = (el: any) => el.key === 'urn';
      // title
      const isTitleProp = (el: any) => el.key === 'title';
      // geometry
      const isGeometryProp = (el: any) => el.key === 'geometry';
      // le props di join le gestisco separatamente
      const isJoinProp = (el: any) => !!joinProperties.find((prop) => prop.key === el.key);
      // props da mostrare
      let properties = data.filter(
        (el: any) => !isCollectionProp(el) && !isJoinProp(el) && !isUrnProp(el) && !isTitleProp(el) && !isGeometryProp(el)
      );

      let urn = data.find((el) => isUrnProp(el));
      if (urn) {
        let id = parseUrn(urn.value).id;
        // Aggiungo la prop id come prima
        properties = [{ key: 'ID', value: id }].concat(properties);
      }
      return { properties, joinProperties };
    } catch (error) {
      console.warn('[FeatureSelectService][parseDataForReportDialogUI]', error);
      return { properties: data, joinProperties: [] };
    }
  }

  // --------------------------------------------------------------------------------

  /**
   * @param collection config_collection dell'asset
   * @param properties proprietà in cui key è l'external_name e value il suo valore
   */
  private getJoinProperties(collection, properties: { key: string; value: any }[]) {
    // estraggo i fields che rappresentano asset in join con quello corrente
    const joinFields = collection.fields.filter((field) => !!field.join);
    // TODO ricordarsi che se abbiamo external_name uguali nei fields di una collection potremmo prendere la prop errata
    // prendo le prop del mio asset che contengono gli asset in join
    return (
      joinFields
        .map((field: any) => properties.find((p) => p.key.toLocaleLowerCase() === field.external_name.toLowerCase()))
        .filter(Boolean)
        // se il value non è un array (perchè value è già trasformato) allora metto come valore []
        .map((el: any) => (Array.isArray(el.value) ? el : { key: el.key, value: [] }))
    );
  }

  // ----------------------------------------------------------------------------------------------------------------

  async getPlacedAssetGeometry(feature: any) {
    // itero finchè non trovo la geometria di oggetti contenitori da far ereditare alla feature
    const featuresWithGeometry = (await this.inheritsGeometryFromParentAsset([feature])).filter((f) => !!f.geometry);
    // se non trovo alcuna feature non è stato effettivamente possibile recuperare la geometria
    if (!featuresWithGeometry.length) {
      return null;
    }
    let isString = featuresWithGeometry.every((el) => el.geometry.type == 'LineString');
    let isPoint = featuresWithGeometry.every((el) => el.geometry.type == 'Point');

    let multiGeom;
    if (isString) {
      multiGeom = {
        type: 'MultiLineString',
        coordinates: []
      };
    }

    if (isPoint) {
      multiGeom = {
        type: 'MultiPoint',
        coordinates: []
      };
    }
    // adesso devo concatenare le geometrie delle features che ho trovato
    // per farlo creo un oggetto geometry di tipo MultiLineString
    // e pusho dentro tutte le geometrie che ho trovato che posseggono una geometria
    // FW: per fastweb i cavi puntuali ereditano la geometria dalle strutture, in questo punto eliminamo i cavi puntuali che non influiscono sulla geometria totale recuperata
    featuresWithGeometry.forEach((f: any) => multiGeom.coordinates.push(f.geometry.coordinates));
    return multiGeom;
  }

  // ----------------------------------------------------------------------------------------------------------------

  async inheritsGeometryFromParentAsset(features: any[]): Promise<any[]> {
    try {
      // per ogni feature ricavo gli urn degli oggetti contenitori da cui vorrei ereditare la geometria
      const urns: string[] = features.reduce((acc, feature) => {
        const urns = this.getContainerAssetUrns(feature);
        return acc.concat(urns);
      }, []);
      // se non ho trovato nessuna feature contenitore esco
      if (!urns.length) {
        return [];
      }
      // recupero le features
      const response = await this.selectByUrns(urns, true);
      // se trovo le feature
      if (response?.result?.features) {
        const parentFeatures: any[] = response.result.features;
        // controllo che ci sia almeno una geometria nelle feature trovate
        // non dovrebbe essere necessario questo controllo poichè sono tutte dello stesso tipo
        // e quindi se la trovo nella prima la troverò in tutte
        const atLeastOneGeometry = parentFeatures.some((feature) => !!feature.geometry);
        if (atLeastOneGeometry) {
          return parentFeatures;
        }
        // se non ho geometrie itero il processo sulle feature trovate
        return this.inheritsGeometryFromParentAsset(parentFeatures);
      }
      // se non trovo features
      return [];
    } catch (error) {
      console.error('[FeatureSelectService][inheritsGeometryFromParentAsset]', error);
      return [];
    }
  }

  // ----------------------------------------------------------------------------------------------------------------

  getContainerAssetUrns(feature: any): string[] {
    // recupero la collection a cui appartiene la feature
    const { config_collection } = this.getConfigDataFromUrn(feature.id);
    if (!config_collection) {
      console.warn(`[FeatureSelectService][getContainerAssetUrns] Nessuna configurazione per l'asset ${feature.id}`);
      return [];
    }
    // ne estraggo la prop 'fields' che contiene tutti i campi ad essa collegati
    const fields = config_collection.fields;
    // identifico il campo (qualora ci fosse) che risulta essere il contenitore della feature corrente
    const fieldContainer = fields.find((field) => field.is_container);
    // se non ho asset contenitori di questo, esco
    if (!fieldContainer) {
      return [];
    }
    // recupero il nome del campo
    const fieldName = fieldContainer.name;
    // con il nome del campo recupero gli asset correlati a questo
    const objects = feature.properties[fieldName];
    // parsifico gli oggetti a JSON e ne estraggo gli urn (scarto quelli non parsabili)
    return objects.map((text: string) => safeJsonParse(text)?.urn).filter((urn: string | undefined) => !!urn);
  }

  // ----------------------------------------------------------------------------------------------------------------

  async selectByUrns(urns: string[], geoms_only = false): Promise<any> {
    try {
      const array = [];
      const offset = 500;
      while (urns.length > 0) {
        array.push(urns.splice(0, offset));
      }
      let responses: any = [];

      for (let i = 0; i < array.length; i++) {
        // concateno gli urn in un unica stringa
        const stringUrns = array[i].reduce((acc: string, urn: string) => (acc ? acc + ',' + urn : urn), '');
        const url = this._apiUrlService.createUrl(API_URLS.MAP.CORE.SELECT_BY_URNS);
        const body = { urns: stringUrns, geoms_only: geoms_only };
        const request = this._utilsService.postRequest(url, body, false, {}, 'application/json');
        // reupero tutte le feature identificate dagli urns
        const response = (await lastValueFrom(request)) as any;
        if (!response?.result) {
          throw new Error();
        }
        responses.push(response?.result?.features);
      }

      return {
        result: {
          type: 'FeatureCollection',
          crs: {
            type: 'name',
            properties: {
              name: 'EPSG:4326'
            }
          },
          bbox: [],
          features: responses.flat()
        }
      };
    } catch (error) {
      console.error('[FeatureSelectService][selectByUrns]', error);
      return null;
    }
  }

  // --------------------------------------------------------------------------------------------------------------------

  getHttpOptions() {
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + sessionStorage.getItem(LoginService.AUTH_TOKEN_KEY)
    });
    return { headers };
  }

  notifyFeaturesSelectedChange() {
    this.featuresSelectedChanged$.next(this.featuresSelected);
  }

  public async fetchFeatureByUrn(urn: string): Promise<Feature | null> {
    try {
      const response = await this.selectByUrns([urn]);
      if (!response?.result?.features?.length) {
        return null;
      }
      const feature = new GeoJSON().readFeatures(response.result)[0];
      if (!feature) {
        return null;
      }
      await this.prepareFeatureObject(feature, urn);
      return feature;
    } catch (error) {
      console.error('[FeatureSelectService][fetchFeatureByUrn]', error);
      return null;
    }
  }

  async prepareFeatureObject(feature: Feature<any>, urn = null): Promise<void> {
    const { config_collection } = this.getConfigDataFromUrn('' + feature.getId());
    if (urn) {
      feature.set('urn', urn);
    }

    feature.set('id', parseUrn(feature.getId() as string).id);
    feature.set('detailUrl', this._apiUrlService.createUrl(API_URLS.MAP.CORE.SELECT_BY_URN));
  }

  public parseFeature(feature) {
    if (feature === null) {
      return null;
    }

    const data = safeJsonClone(feature);
    const keys = Object.keys(data);

    for (let i = 0; i < keys.length; i++) {
      if (data[keys[i]]) {
        if (this._isArray(data[keys[i]])) {
          for (let k = 0; k < data[keys[i]].length; k++) {
            if (this._isJson(data[keys[i]][k])) {
              try {
                data[keys[i]][k] = this.parseFeature(JSON.parse(decodeURI(data[keys[i]][k])));
              } catch (e) {
                // decodeURI può fallire anche quando la stringa originale è JSON valido (es. sequenze % malformate) — lascio il valore grezzo
              }
            }
          }
        } else if (this._isJson(data[keys[i]]) && typeof data[keys[i]] !== 'string') {
          try {
            data[keys[i]] = this.parseFeature(JSON.parse(data[keys[i]]));
          } catch (e) {
            // lascio il valore grezzo se il parse fallisce
          }
        }
      }
    }
    return data;
  }

  private _isJson(str) {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  }

  private _isArray(value: unknown) {
    return value instanceof Array;
  }

  // method for checking if an object has a field Case Insensitive
  private _hasOwnPropertyCI(obj, prop) {
    const lowerProp = prop.toLowerCase();
    return Object.keys(obj).some((key) => key.toLowerCase() === lowerProp);
  }

  // method for retrieving the value of a field in an object Case Insensitive
  private _getCI(obj, key) {
    const lowerKey = key.toLowerCase();
    const actualKey = Object.keys(obj).find((k) => k.toLowerCase() === lowerKey);
    return actualKey !== undefined ? obj[actualKey] : undefined;
  }
}

interface MyObject {
  [key: string]: any;
}
